/** Recording and restreaming (INT-WEB-05, INT-SCR-04, INT-STR-01…04, INT-STR-06): response shapes shared by the screens. */
import type { DestinationKind, Overlay, OutputStatus, PublishStatus, RecordingStatusCode, StreamPrivacy } from '@vtf/shared';

export interface SetupState {
  configured: boolean;
  message: string | null;
}

export interface Destination {
  id: string;
  kind: DestinationKind;
  kindLabel: string;
  name: string;
  url: string | null;
  /** "••••WXYZ" — never the key */
  streamKey: string | null;
  channel: { id: string | null; title: string | null; handle: string | null } | null;
  createdAt: string;
  createdBy: { id: string; name: string } | null;
}

export interface DestinationsData {
  canManage: boolean;
  egress: SetupState;
  youtube: SetupState & { redirectUri: string };
  destinations: Destination[];
}

export interface BroadcastOutput {
  id: string;
  destination: { id: string; kind: DestinationKind; kindLabel: string; name: string; streamKey: string | null; removed: boolean };
  status: OutputStatus;
  statusLabel: string;
  lastError: string | null;
  attempts: number;
  videoUrl: string | null;
  startedAt: string | null;
  liveAt: string | null;
  endedAt: string | null;
  canStart: boolean;
  canStop: boolean;
}

export interface BroadcastData {
  webinarId: string;
  webinarStatus: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  setup: { egress: SetupState; youtube: SetupState; recording: SetupState };
  broadcast: { title: string; description: string | null; privacy: StreamPrivacy; slate: 'START' | 'END' | null; outputs: BroadcastOutput[] } | null;
  destinations: Destination[];
  links: { youtube: string | null; facebook: string | null };
  overlay: (Overlay & { status: string }) | null;
}

export interface RecordingView {
  id: string;
  webinarId: string | null;
  roomId: string | null;
  status: RecordingStatusCode;
  statusLabel: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  sizeBytes: number | null;
  trimStartSeconds: number | null;
  trimEndSeconds: number | null;
  error: string | null;
  libraryItem: { id: string; status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' } | null;
  /** INT-REC-01/04: the YouTube publish package, once there is one */
  publishPackage: { id: string; status: PublishStatus; youtubeUrl: string | null } | null;
  canManage: boolean;
}

export interface RecordingList extends SetupState {
  recording?: boolean;
  recordings: RecordingView[];
}

export interface Playback {
  url: string;
  expiresAt: string;
  trimStartSeconds: number | null;
  trimEndSeconds: number | null;
  fragment: string;
  durationSeconds: number | null;
}

/** The recording behind a RECORDING library item. */
export interface LibraryRecording {
  id: string;
  status: RecordingStatusCode;
  durationSeconds: number | null;
  trimStartSeconds: number | null;
  trimEndSeconds: number | null;
  recordedAt: string;
  webinarId: string | null;
  links: { youtube: string | null; facebook: string | null };
  materials: { id: string; title: string; type: string }[];
}

export const fmtBytes = (n: number | null) => (n === null ? '—' : n >= 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
